/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, Input, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { FuncionBoton, GrupoBotones, IBoton } from 'modelos/src';
import { firstValueFrom } from 'rxjs';
import { BotonesService } from '../../../botones/botones.service';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import {
  BotoneraTemplateId,
  hintTamanoBoton,
  textoHint,
} from '../../../../auxiliares/plantillas/plantillas-spec';

/**
 * Factory del FormGroup de un botón del provisionador. Subconjunto enfocado en "cargar datos":
 * lo esencial para definir la funcionalidad. Los campos avanzados (trackeo, multimedia, formulario
 * de evento) se editan luego en el editor de botón completo.
 */
export function crearGrupoBoton(
  fb: UntypedFormBuilder,
  tmpId: string,
  data?: Partial<IBoton>,
): UntypedFormGroup {
  return fb.group({
    _tmpId: [tmpId],
    nombre: [data?.nombre, Validators.required],
    variante: [data?.variante],
    funcion: [data?.funcion ?? 'Alerta', Validators.required],
    // Modo imagen vs texto
    urlImagen: [data?.urlImagen],
    // Imagen mientras se mantiene apretado (opcional; sin ella la app oscurece `urlImagen`).
    urlImagenPush: [data?.urlImagenPush],
    texto: [data?.texto],
    color: [data?.color],
    // Condicionales por función
    grupoDestino: [data?.grupoDestino], // funcion = Menu
    link: [data?.link], // funcion = Link
    telemedicina: [data?.telemedicina], // funcion = Telemedicina
    // Comunes
    sirena: [data?.sirena],
    global: [data?.global],
    prioridad: [data?.prioridad],
    notificacion: [data?.notificacion],
    codigoSmartCity: [data?.codigoSmartCity],
  });
}

/**
 * Form presentacional de UN botón. NO llama al service de crear/editar ni navega: el guardado lo
 * orquesta el provisionador. Sube la imagen del botón (path público) y patchea el control.
 */
@Component({
  selector: 'app-boton-form',
  templateUrl: './boton-form.component.html',
  styleUrls: ['./boton-form.component.scss'],
  standalone: false,
})
export class BotonFormComponent {
  private service = inject(BotonesService);
  helper = inject(HelperService);

  @Input() group?: UntypedFormGroup;
  @Input() botoneraId?: BotoneraTemplateId;
  /** Cantidad de botones principales (para el hint del template "centrado"). */
  @Input() cantidadPrincipales = 1;

  loading = false;

  public funciones: FuncionBoton[] = [
    'Alerta',
    'Evento',
    'Sirena',
    'Reflector',
    '911',
    'Link',
    'Alerta por Punto',
    'Telemedicina',
    'Menu',
  ];

  public gruposBotones: GrupoBotones[] = ['Principales', 'Secundarios', 'Otros'];
  public notificaciones = ['Sirena', 'Campana', 'Silencio'];

  get funcion(): FuncionBoton | undefined {
    return this.group?.get('funcion')?.value;
  }

  /** Texto del hint de tamaño de imagen según la botonera elegida. */
  get hint(): string {
    return textoHint(hintTamanoBoton(this.botoneraId, this.cantidadPrincipales));
  }

  public onColorChange(event: string, control: string): void {
    this.group?.get(control)?.patchValue(event);
  }

  /** Sube la imagen del botón a un path público y patchea `urlImagen`. */
  public async setFile(event: Event, control: string): Promise<void> {
    const file = (event as any).target?.files?.item(0);
    if (!file) return;
    const nombre = this.group?.get('nombre')?.value || 'boton';
    const variante = this.group?.get('variante')?.value || 'default';
    const fileName = `botones/${nombre}/${variante}/${control}`;
    this.loading = true;
    try {
      const res = await firstValueFrom(
        this.service.subirImagenPublica(file, fileName),
      );
      const val = res.url + `?${(Math.random() * 1000).toFixed(0)}`;
      this.group?.patchValue({ [control]: val });
    } catch (err) {
      console.error(err);
      this.helper.notifError(err);
    }
    this.loading = false;
  }

  /** Sin imagen normal no tiene sentido la presionada: se borran juntas. */
  public limpiarImagen(control: 'urlImagen' | 'urlImagenPush' = 'urlImagen'): void {
    this.group?.patchValue(
      control === 'urlImagen'
        ? { urlImagen: null, urlImagenPush: null }
        : { urlImagenPush: null },
    );
  }
}
